import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Name } from './const';
import { MainBox } from './styles';
import { DeclarationProps } from './types';
import DeclarationViewSkeleton from './Skeletons/declarationViewSkeleton';
import WithMenu from '10.quickConnect.app/components/shared/LayOut/WithMenu';
import useData from '10.quickConnect.app/components/domain/Declaration/hooks';
import DeclarationContainer from '20.formLib/DeclarationContainer';
import { terraCotta400 } from '00.app/theme';
import { DeclarationProvider } from '10.quickConnect.app/components/domain/Declaration/context/declarationContext';
import { ErrorComponent } from '10.quickConnect.app/components/shared/ErrorBoundary';
import OuSwitchPrompt from '10.quickConnect.app/components/shared/Dialogs/OuSwitchPrompt';
import OpenedElsewherePrompt from '10.quickConnect.app/components/shared/Dialogs/OpenedElsewherePrompt';
import { useStore } from '30.quickConnect.Stores';

const Declaration = ({ isDuplicated, isDeepLink }: DeclarationProps) => {
  const { t } = useTranslation('declaration');
  const { t: tForms } = useTranslation('forms');
  const scrollTarget = useRef<HTMLElement>();
  const {
    context,
    entityData,
    declaration,
    flattenFieldsScoped,
    flattenFields,
    declarationData,
    setDeclaration,
    setDeclarationData,
    currentActivityId,
    inboxId,
    declarationId,
    isEditable,
    isDeletedDeclarationContext,
    formUnauthorized,
    initFieldError,
    missingSharedListIdsNotLocal,
    hasFormRetrievalError,
    promptProps,
    openedElsewherePromptProps,
  } = useData(isDuplicated, isDeepLink);
  const {
    DeclarationStore: { contextRetrievalErrorMessage },
  } = useStore();
  if (formUnauthorized) {
    return <ErrorComponent error={tForms('qacapp_error_form_not_available_or_unauthorized')} />;
  } else if (hasFormRetrievalError) {
    return (
      <ErrorComponent
        error={tForms('qacapp_error_context_while_retrieving').toString()}
        details={contextRetrievalErrorMessage}
      />
    );
  } else if (!!initFieldError === true) {
    return (
      <ErrorComponent
        error={tForms('qacapp_error_form_failed_init', {
          field: initFieldError,
        }).toString()}
      />
    );
  } else if (missingSharedListIdsNotLocal.length > 0) {
    return (
      <ErrorComponent
        error={tForms('qacapp_error_sharedlists_missing_with_ids', {
          ids: missingSharedListIdsNotLocal.toLocaleString(),
        }).toString()}
      />
    );
  }
  return (
    <>
      <WithMenu
        data-cy={Name}
        pageTitle={context ? `${t('qcapp_declaration_title')} : ${context.name}` : t('qcapp_declaration_title')}
      >
        <Box sx={MainBox} ref={scrollTarget}>
          <div id={`scrollToAnchor-${Name}`} />
          {context && declaration ? (
            <DeclarationProvider value={declaration}>
              <DeclarationContainer
                context={context}
                entityData={entityData}
                declaration={declaration}
                flattenFieldsScoped={flattenFieldsScoped}
                flattenFields={flattenFields}
                declarationData={declarationData}
                setDeclarationData={setDeclarationData}
                setDeclaration={setDeclaration}
                activityId={currentActivityId}
                inboxId={inboxId}
                declarationId={declarationId}
                isEditable={isEditable}
              />
            </DeclarationProvider>
          ) : isDeletedDeclarationContext ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', color: `${terraCotta400}` }}>
              {t('qcapp_warning_error_loading_dcl')}
              {contextRetrievalErrorMessage && (
                <>
                  <br />
                  {contextRetrievalErrorMessage}
                </>
              )}
            </Box>
          ) : (
            <DeclarationViewSkeleton />
          )}
        </Box>
      </WithMenu>
      <OuSwitchPrompt {...promptProps} />
      <OpenedElsewherePrompt {...openedElsewherePromptProps} />
    </>
  );
};
export default React.memo(observer(Declaration));
